import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';

type Profession = Tables<'professions'>;

interface ProfessionAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export function ProfessionAutocomplete({ value, onChange, placeholder = "Search for your profession...", className }: ProfessionAutocompleteProps) {
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [filteredProfessions, setFilteredProfessions] = useState<Profession[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch professions from database
  useEffect(() => {
    const fetchProfessions = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('professions')
          .select('*')
          .order('name');

        if (error) {
          console.error('Error fetching professions:', error);
        } else {
          setProfessions(data || []);
        }
      } catch (error) {
        console.error('Error fetching professions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfessions();
  }, []);

  // Filter professions based on input
  useEffect(() => {
    if (!value) {
      setFilteredProfessions(professions);
    } else {
      const filtered = professions.filter(profession =>
        profession.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredProfessions(filtered);
    }
  }, [value, professions]);

  const handleSelect = (profession: Profession) => {
    onChange(profession.name);
    setOpen(false);
  };

  const handleInputChange = (inputValue: string) => {
    onChange(inputValue);
    setOpen(true);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input
          placeholder={placeholder}
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          className={cn("bg-white text-black placeholder:text-[#b0b0b0] border-black font-sans", className)}
        />
      </div>
      
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-black rounded-md shadow-lg max-h-60 overflow-auto">
          {loading ? (
            <div className="p-4 text-center text-sm text-gray-500">
              Loading professions...
            </div>
          ) : filteredProfessions.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">
              No professions found
            </div>
          ) : (
            <div className="py-1">
              {filteredProfessions.map((profession) => (
                <div
                  key={profession.id}
                  className="relative flex cursor-pointer select-none items-center px-4 py-2 hover:bg-gray-100"
                  onClick={() => handleSelect(profession)}
                >
                  <span className="text-sm font-medium text-black !text-black" style={{ color: 'black !important' }}>{profession.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function cn(...classes: (string | undefined)[]) {
  return classes.filter(Boolean).join(' ');
} 